import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import api from '@/lib/axios'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Shield, RefreshCw } from 'lucide-react'

interface AuthLogRow {
  id: string
  user_id: string | null
  user_email: string | null
  user_full_name: string | null
  org_id: string | null
  event_type: string
  email: string | null
  ip_address: string | null
  user_agent: string | null
  metadata: Record<string, unknown> | null
  created_at: string | null
}

interface AuthLogPage {
  items: AuthLogRow[]
  total: number
  page: number
  page_size: number
  total_pages: number
}

const eventColor: Record<string, string> = {
  login_success: 'bg-green-100 text-green-800 border-green-200',
  login_failed: 'bg-red-100 text-red-800 border-red-200',
  token_refresh: 'bg-blue-50 text-blue-700 border-blue-200',
  org_switch: 'bg-purple-50 text-purple-700 border-purple-200',
  impersonate_start: 'bg-amber-100 text-amber-900 border-amber-300',
  impersonate_end: 'bg-amber-50 text-amber-800 border-amber-200',
  password_reset_requested: 'bg-orange-50 text-orange-700 border-orange-200',
  password_reset_completed: 'bg-orange-100 text-orange-800 border-orange-300',
  logout: 'bg-gray-100 text-gray-700 border-gray-200',
}

function eventBadge(event_type: string) {
  return eventColor[event_type] || 'bg-gray-100 text-gray-700 border-gray-200'
}

function formatTimestamp(s: string | null): string {
  if (!s) return '—'
  try {
    const d = new Date(s)
    return d.toLocaleString()
  } catch {
    return s
  }
}

export default function AdminAuthLogs() {
  const [eventType, setEventType] = useState<string>('')
  const [emailFilter, setEmailFilter] = useState<string>('')
  const [sinceDays, setSinceDays] = useState<string>('30')
  const [page, setPage] = useState(1)
  const pageSize = 50

  const { data: eventTypes = [] } = useQuery({
    queryKey: ['admin-auth-log-event-types'],
    queryFn: () =>
      api.get<{ event_type: string; count: number }[]>('/admin/auth-logs/event-types').then((r) => r.data),
    staleTime: 60 * 1000,
  })

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['admin-auth-logs', page, eventType, emailFilter, sinceDays],
    queryFn: () =>
      api
        .get<AuthLogPage>('/admin/auth-logs', {
          params: {
            page,
            page_size: pageSize,
            ...(eventType ? { event_type: eventType } : {}),
            ...(emailFilter ? { email_contains: emailFilter } : {}),
            ...(sinceDays && sinceDays !== 'all' ? { since_days: sinceDays } : {}),
          },
        })
        .then((r) => r.data),
  })

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-muted-foreground" />
          <h1 className="text-2xl font-bold">Auth Audit Logs</h1>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw className={`h-4 w-4 mr-1 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Event Type</Label>
              <Select
                value={eventType || 'all'}
                onValueChange={(v) => {
                  setEventType(v === 'all' ? '' : v)
                  setPage(1)
                }}
              >
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="All events" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All events</SelectItem>
                  {eventTypes.map((et) => (
                    <SelectItem key={et.event_type} value={et.event_type}>
                      {et.event_type} ({et.count})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Email contains</Label>
              <Input
                value={emailFilter}
                onChange={(e) => {
                  setEmailFilter(e.target.value)
                  setPage(1)
                }}
                placeholder="user@example.com"
                className="h-9"
              />
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Time range</Label>
              <Select
                value={sinceDays}
                onValueChange={(v) => {
                  setSinceDays(v)
                  setPage(1)
                }}
              >
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Last 24 hours</SelectItem>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                  <SelectItem value="all">All time</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-end">
              <span className="text-sm text-muted-foreground">
                {data ? `${data.total} event${data.total !== 1 ? 's' : ''}` : '—'}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="text-center py-12 text-muted-foreground">Loading auth logs...</div>
          ) : !data || data.items.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">No events match the current filters.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-3 font-medium whitespace-nowrap">When</th>
                    <th className="text-left p-3 font-medium whitespace-nowrap">Event</th>
                    <th className="text-left p-3 font-medium">User</th>
                    <th className="text-left p-3 font-medium whitespace-nowrap">IP</th>
                    <th className="text-left p-3 font-medium">User-Agent</th>
                    <th className="text-left p-3 font-medium">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((row) => (
                    <tr key={row.id} className="border-t hover:bg-muted/20">
                      <td className="p-3 whitespace-nowrap text-muted-foreground">{formatTimestamp(row.created_at)}</td>
                      <td className="p-3">
                        <Badge variant="outline" className={`text-xs ${eventBadge(row.event_type)}`}>
                          {row.event_type}
                        </Badge>
                      </td>
                      <td className="p-3">
                        {row.user_email ? (
                          <div className="min-w-0">
                            <div className="font-medium truncate" title={row.user_email}>{row.user_email}</div>
                            {row.user_full_name && (
                              <div className="text-xs text-muted-foreground truncate">{row.user_full_name}</div>
                            )}
                          </div>
                        ) : row.email ? (
                          <div className="text-muted-foreground italic" title={row.email}>{row.email}</div>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="p-3 whitespace-nowrap text-muted-foreground font-mono text-xs">
                        {row.ip_address || '—'}
                      </td>
                      <td className="p-3 text-xs text-muted-foreground max-w-[260px] truncate" title={row.user_agent || ''}>
                        {row.user_agent || '—'}
                      </td>
                      <td className="p-3 text-xs text-muted-foreground max-w-xs">
                        {row.metadata ? (
                          <code className="block truncate" title={JSON.stringify(row.metadata)}>
                            {JSON.stringify(row.metadata)}
                          </code>
                        ) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {data && data.total_pages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            Page {data.page} of {data.total_pages}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(data.total_pages, p + 1))}
              disabled={page >= data.total_pages}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
